/**
 * Recipients CRUD: who gets alerts and nudges, by text or Slack. Edits live
 * in the page draft until Save. The Slack connection banner sits at the top
 * because it decides whether the Slack channel option is usable.
 */
import { useId } from 'react';
import { Plus } from 'lucide-react';
import type { Role } from '@/features/pipeline/types';
import { RecipientRow } from './RecipientRow';
import { SlackConnectionBanner } from './SlackConnectionBanner';
import type { ChannelsState } from './SlackChannelPicker';
import { newRecipient, type ConnectionsStatus, type Recipient, type RecipientField, type SettingsErrors, type SlackStatus } from './types';

export interface RecipientTouch {
  id: string;
  field: RecipientField;
}

export function RecipientsList({
  recipients,
  roles,
  errors,
  connections,
  connectionsError,
  slack,
  channels,
  onChange,
}: {
  recipients: Recipient[];
  roles: Role[];
  errors: SettingsErrors['recipients'];
  connections: ConnectionsStatus | undefined;
  connectionsError: boolean;
  slack: SlackStatus;
  channels: ChannelsState;
  onChange: (next: Recipient[], touched?: RecipientTouch) => void;
}) {
  const id = useId();
  const update = (next: Recipient, field: RecipientField) =>
    onChange(recipients.map((r) => (r.id === next.id ? next : r)), { id: next.id, field });
  const remove = (rid: string) => onChange(recipients.filter((r) => r.id !== rid));

  return (
    <section aria-labelledby={`${id}-h`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id={`${id}-h`} className="text-[15px] font-semibold text-gray-900">Recipients</h2>
          <p className="mt-1 text-[13px] text-muted">
            The people who get candidate alerts and “Have you spoken to…?” nudges, by text or in a Slack channel. Pick the roles each person covers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange([...recipients, newRecipient()])}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[13px] font-medium text-navy hover:bg-page"
        >
          <Plus size={15} strokeWidth={2.2} aria-hidden /> Add recipient
        </button>
      </div>
      <div className="mt-3">
        <SlackConnectionBanner connections={connections} isError={connectionsError} slack={slack} />
      </div>
      {recipients.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-gray-300 px-3 py-4 text-center text-[13px] text-muted">
          Nobody is set up yet. Add the managers who should hear about new candidates.
        </p>
      ) : (
        <ul className="mt-3 space-y-3" aria-label="Recipients">
          {recipients.map((r) => (
            <RecipientRow key={r.id} recipient={r} roles={roles} channels={channels} errors={errors[r.id]} onChange={update} onRemove={() => remove(r.id)} />
          ))}
        </ul>
      )}
    </section>
  );
}
